'use client';

import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface SetupStepSectionProps {
  title: string;
  description?: string;
  statusBadge?: string;
  statusMeta?: string;
  titleAdornment?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

export function SetupStepSection({
  title,
  description,
  statusBadge,
  statusMeta,
  titleAdornment,
  children,
  footer,
}: SetupStepSectionProps) {
  return (
    <section className="border-border bg-card rounded-xl border p-0 shadow-[0_1px_2px_color-mix(in_srgb,var(--foreground)_4%,transparent)]">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 border-b border-[color-mix(in_srgb,var(--border)_80%,transparent)] px-8 pb-6 pt-8">
        <div className="min-w-0 flex-[1_1_16rem]">
          <div className="flex w-full flex-wrap items-baseline justify-between gap-x-4 gap-y-3">
            <div className="flex min-w-0 items-center gap-3">
              {titleAdornment}
              <h2 className="text-foreground m-0 text-xl font-semibold leading-tight tracking-[-0.025em] sm:text-2xl">
                {title}
              </h2>
            </div>
          </div>
          {description ? (
            <p className="text-muted-foreground mt-2 max-w-[42rem] text-[0.8125rem] leading-[1.45]">
              {description}
            </p>
          ) : null}
        </div>
        {statusBadge || statusMeta ? (
          <div className="shrink-0 text-right">
            {statusBadge ? (
              <span className="text-primary inline-block rounded-full bg-[var(--crm-primary-surface)] px-[0.5625rem] py-[0.1875rem] text-[0.6875rem] font-semibold tracking-[0.01em]">
                {statusBadge}
              </span>
            ) : null}
            {statusMeta ? (
              <p
                className={cn(
                  'text-muted-foreground mt-1.5 text-[0.6875rem]',
                  !statusBadge && 'm-0 font-medium',
                )}
              >
                {statusMeta}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="min-w-0 px-8 py-6">{children}</div>
      {footer ? (
        <div className="bg-card sticky bottom-0 z-10 rounded-b-xl border-t border-[color-mix(in_srgb,var(--border)_80%,transparent)] px-8 pb-8 pt-6">
          {footer}
        </div>
      ) : null}
    </section>
  );
}
